import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Spinner } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function BrandStyle() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedWords, setSelectedWords] = useState([]);
  const [saving, setSaving] = useState(false);

  const styleOptions = [
    'Elegant',
    'Tech',
    'Minimal',
    'Flowing',
    'Angled',
    'Glamorous',
    'Old Fashioned',
    'Cosmopolitan'
  ];

  useEffect(() => {
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializePage = async () => {
    setLoading(true);

    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Load saved selections
    await fetchSavedStyle();

    setLoading(false);
  };

  const fetchSavedStyle = async () => {
    try {
      const response = await fetch('/api/brand/get-saved-brand-style', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.brand_style && data.brand_style.selected_words) {
          setSelectedWords(data.brand_style.selected_words);
        }
      }
      // If 404, no saved style - that's okay
    } catch (err) {
      console.log('No saved style found or error fetching:', err);
    }
  };

  const toggleWord = (word) => {
    if (selectedWords.includes(word)) {
      // Deselect
      setSelectedWords(selectedWords.filter(w => w !== word));
    } else {
      // Select (limit to 3)
      if (selectedWords.length >= 3) {
        setError('You can only select up to 3 words');
        setTimeout(() => setError(''), 3000);
        return;
      }
      setSelectedWords([...selectedWords, word]);
    }
  };

  const handleSaveAndContinue = async () => {
    if (selectedWords.length === 0) {
      setError('Please select at least 1 word');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/brand/save-brand-style', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ selected_words: selectedWords })
      });

      if (response.ok) {
        navigate('/brand-texture');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save brand style');
      }
    } catch (err) {
      setError('Error saving brand style: ' + err.message);
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="branding" />
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          <Card className="shadow">
            <Card.Body className="p-5">
              <h1 className="text-center mb-3">Brand Style</h1>
              <p className="text-center text-muted mb-4">
                Describe the style of your brand.
                Select <strong>1-3 words</strong> that define your brand's style.
              </p>

              {error && <Alert variant="danger">{error}</Alert>}

              {selectedWords.length > 0 && (
                <Alert variant="info" className="text-center mb-4">
                  <strong>{selectedWords.length} of 3 words selected</strong>
                  {selectedWords.length < 3 && ' - You can select more'}
                </Alert>
              )}

              <Row className="g-3 mb-4 justify-content-center">
                {styleOptions.map((word, idx) => {
                  const isSelected = selectedWords.includes(word);
                  return (
                    <Col key={idx} xs={6} md={4} lg={3}>
                      <Card
                        onClick={() => toggleWord(word)}
                        style={{
                          cursor: 'pointer',
                          border: isSelected ? '3px solid #0d6efd' : '2px solid #dee2e6',
                          backgroundColor: isSelected ? '#e7f1ff' : 'white',
                          transition: 'all 0.2s',
                          height: '100%',
                          minHeight: '100px'
                        }}
                        className="h-100 d-flex align-items-center justify-content-center"
                      >
                        <Card.Body className="text-center p-3">
                          {isSelected && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                backgroundColor: '#0d6efd',
                                color: 'white',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                fontWeight: 'bold'
                              }}
                            >
                              ✓
                            </div>
                          )}
                          <p className={`mb-0 ${isSelected ? 'fw-bold text-primary' : ''}`}>
                            {word}
                          </p>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>

              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/dashboard/branding')}
                >
                  ← Back to Dashboard
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  disabled={saving || selectedWords.length === 0}
                  onClick={handleSaveAndContinue}
                >
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    'Save & Continue →'
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default BrandStyle;
